import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress'
import { refreshChannelPosts, selectFullPostsByChannelId, selectPostsStatusByChannelId,
         setChannelRead, selectChannel, addViewingChannelId, removeViewingChannelId,
         fetchOlderChannelPosts} from 'app/store/channelsSlice';
import { subscribeToChannel } from 'app/store/subscriptionSlice';
import usePrevious from '@fuse/hooks/usePrevious';
import usePageVisibility from '../util/usePageVisibility';
import ChatEntry from './ChatEntry';
import ChatInput from './ChatInput'
import ReferralInterestDialog from '../referral/ReferralInterestDialog';



function ChannelChat(props) {
  const dispatch = useDispatch();
  const { channelId, viewCommentsHandler, disableComments, parentPostId, isNotes, isView, initialMessage, textOnly } = props;
  const chatChannelId= channelId;
  const channel = useSelector(selectChannel(channelId));
  const needsRefresh= channel?.needsRefresh;
  const rootChannelId = channel?.rootId;
  const rootChannel = useSelector(selectChannel(rootChannelId));
  const unread= channel?.unread;
  const channelType= rootChannel?.type;
  const channelEntity= rootChannel?.entity;
  const posts= useSelector(selectFullPostsByChannelId(channelId));
  const postsStatus= useSelector(selectPostsStatusByChannelId(channelId));
  const permissions= channel ? channel.callerPermissions : null;
  const chatSize= posts ? posts.length : 0;
  const chatRef = useRef(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const groupWindowInMS = 600000; //10 minutes
  const emptyMessage= initialMessage ? initialMessage : isView ? "" : "Start a conversation by typing your message below.";
  const lastMessage= posts ? posts[posts.length - 1] : undefined;
  const lastMessageTimestamp= lastMessage?.createdAt;
  const [previousLastMessageTimestamp, setPrevioustLastMessageTimestamp]= useState(undefined);
  const newMessages= lastMessageTimestamp != previousLastMessageTimestamp;
  const pageVisible= usePageVisibility();
  const isOrgReferralChan=  channelType === "organization" && channelEntity?.referralChannelId === chatChannelId;
  
  useEffect(() => {
	 // Subscribe to notifications
   if (chatChannelId && !isNotes ){
	    const parentPostChannelId= (!parentPostId || !channel) ? null : channel.parentId; 
	   	dispatch(subscribeToChannel({channelId: chatChannelId, parentPostChannelId: parentPostChannelId, enqueueSnackbar: enqueueSnackbar, closeSnackbar: closeSnackbar}));
   }
  }, [dispatch, chatChannelId, parentPostId]);
  
  useEffect(() => {
	 // Set viewing channel
   if (chatChannelId && !isNotes){
  		if (pageVisible) dispatch(addViewingChannelId(chatChannelId));
  		else dispatch(removeViewingChannelId(chatChannelId));
   }
    return () => {
        // remove viewing channel. subscription cleanup is handled in subscription manager middleware
        dispatch(removeViewingChannelId(chatChannelId));
    };
  }, [dispatch, chatChannelId, pageVisible]);
  
  useEffect(() => {
	if (chatChannelId && needsRefresh){
   	   dispatch(refreshChannelPosts({channelId: chatChannelId}));
   	}
  }, [dispatch, chatChannelId, needsRefresh]);

  useEffect(() => {
    if (newMessages && pageVisible){
  		setTimeout(scrollToBottom, 500);
  		if (unread && !isNotes) dispatch(setChannelRead({channelId: chatChannelId }));
  		setPrevioustLastMessageTimestamp(lastMessageTimestamp);
  	} 
  }, [dispatch, newMessages, pageVisible]);
  
 
  
  
  function scrollToBottom() {
    if (!chatRef.current) {
      return;
    }
    
    chatRef.current.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: 'instant',
    });
  }
  
  function handleScroll(event){
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    const scroll = scrollHeight - scrollTop - clientHeight;

    if (scroll == 0){
    }else if (scrollTop == 0){
  	  dispatch(fetchOlderChannelPosts({channelId: chatChannelId}));
  	}
  }

  function isFirstMessageOfGroup(item, i) {
    return i === 0 || (posts[i - 1] && 
                       (posts[i - 1].author.id !== item.author.id || 
                       Math.abs(posts[i - 1].createdAt - item.createdAt) > groupWindowInMS));
  }

  function isLastMessageOfGroup(item, i) {
    return i === posts.length - 1 || (posts[i + 1] &&
                                    (posts[i + 1].author.id !== item.author.id ||
                                     Math.abs(posts[i + 1].createdAt - item.createdAt) > groupWindowInMS));
  }

  if (!chatChannelId || !posts) {
    return null;
  }

  return (
	
  <div className="flex flex-auto grow shrink-0 basis-0 min-h-0 h-full w-full">  
    <div className={clsx('flex flex-1 z-10 w-full flex-col relative', props.className)}>
      <div ref={chatRef} onScroll={handleScroll} className="flex flex-1 flex-col items-center w-full overflow-y-auto">
        {posts?.length === 0 && (
          <div className="flex flex-col flex-1">
            {(postsStatus === 'loading') && (
          	   <div className="flex flex-col flex-1 items-center justify-items-center mt-10">
                <CircularProgress />
               </div>
            )}
            {(postsStatus != 'loading') && (
	            <Typography className="px-16 py-24 text-center" color="text.secondary">
					{emptyMessage}
	            </Typography>
            )}
          </div>
        )}
		   
        {posts?.length > 0 && (
          <div className="flex flex-col pt-16 px-16 pb-40 w-full">
            {posts.map((post, i) => (
			  <ChatEntry key={post.id} 
			            postId={post.id} 
			            rootChannelId={rootChannelId}  
			            permissions={permissions} 
			            channelType={channelType}
			            firstOfGroup={isFirstMessageOfGroup(post, i)}
			            lastOfGroup={isLastMessageOfGroup(post, i)}
			            lastOfChannel={i + 1 === chatSize}
			            viewCommentsHandler={viewCommentsHandler}
			            disableComments={disableComments}
                         isOrgReferralChan={isOrgReferralChan}
			            />
             ))}
          </div>
        )}
      </div>
      {posts && (
	    <ChatInput channelId={channelId} rootChannelId={rootChannelId} channelType={channelType} parentPostId={parentPostId} isNotes={isNotes} textOnly={textOnly} />
      )}
    </div>
    
    <ReferralInterestDialog />

  </div>
  );
}

export default ChannelChat;
